import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, App, Button, Form, Input, Skeleton, Typography } from 'antd'
import { createSchemaFieldRule } from 'antd-zod'
import type { z } from 'zod'
import { botUpdateSchema } from '@super-mega/server/contracts/bot'
import { useAccess } from '@/hooks/use-access.ts'
import { useTRPC } from '@/trpc/react.ts'
import type { BotDetailData } from './bot-detail.tsx'

const basicRule = createSchemaFieldRule(botUpdateSchema)
type Props = { bot: BotDetailData; onSaved: () => Promise<void> }

export function BotBasicInfo(props: Props) {
  const trpc = useTRPC()
  const access = useAccess()
  const canReadCredentials = access.can('bot.credential.update')
  const credentials = useQuery({
    ...trpc.bot.credentials.queryOptions({ id: props.bot.id }),
    enabled: canReadCredentials,
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false
  })
  if (canReadCredentials && credentials.isPending) return <Skeleton active />
  if (canReadCredentials && credentials.error) {
    return (
      <Alert
        type="error"
        showIcon
        title={credentials.error.message}
        action={<Button onClick={() => void credentials.refetch()}>重新载入</Button>}
      />
    )
  }
  return <BasicInfoForm {...props} secret={credentials.data?.secret} />
}

function BasicInfoForm({ bot, onSaved, secret }: Props & { secret?: string }) {
  const trpc = useTRPC()
  const access = useAccess()
  const { message } = App.useApp()
  const cache = useQueryClient()
  const canEditCredentials = access.can('bot.credential.update')
  const update = useMutation(
    trpc.bot.update.mutationOptions({
      onSuccess: async () => {
        await cache.invalidateQueries(trpc.bot.credentials.queryFilter({ id: bot.id }))
        await onSaved()
        void message.success('基本信息已保存；企微连接变更后请重启对应 Hermes 网关')
      }
    })
  )
  return (
    <section className="bg-surface border-border overflow-hidden rounded-xl border">
      <div className="border-border border-b px-5 py-4 sm:px-6">
        <h2 className="m-0 text-base font-semibold">基本信息</h2>
        <p className="text-content-muted mt-1 mb-0 text-sm">管理机器人资料与企业微信连接。</p>
      </div>
      <Form<z.infer<typeof botUpdateSchema>>
        layout="vertical"
        initialValues={{ name: bot.name, remark: bot.remark, wecomBotId: bot.wecomBotId, secret }}
        disabled={!access.can('bot.update') || update.isPending}
        scrollToFirstError={{ focus: true }}
        onFinish={(values) =>
          update.mutate({
            name: values.name,
            remark: values.remark,
            id: bot.id,
            expectedVersion: bot.version,
            ...(canEditCredentials ? { wecomBotId: values.wecomBotId, secret: values.secret } : {})
          })
        }
      >
        <div className="p-5 sm:p-6">
          {update.error && <Alert type="error" showIcon title={update.error.message} className="mb-4!" />}
          <div className="grid gap-x-8 lg:grid-cols-2">
            <Form.Item name="name" label="机器人名称" rules={[basicRule]} required>
              <Input maxLength={100} placeholder="请输入机器人名称" />
            </Form.Item>
            <Form.Item label="机器人 ID">
              <div className="bg-surface-muted border-border rounded-md border px-3 py-1.5">
                <Typography.Text copyable className="font-mono text-sm! break-all">
                  {bot.id}
                </Typography.Text>
              </div>
            </Form.Item>
            <Form.Item name="wecomBotId" label="企微机器人 ID" rules={[basicRule]} required>
              <Input
                disabled={!canEditCredentials || !access.can('bot.update') || update.isPending}
                maxLength={255}
                autoComplete="off"
              />
            </Form.Item>
            {canEditCredentials && (
              <Form.Item name="secret" label="企微 Secret" rules={[basicRule]} required>
                <Input
                  maxLength={4096}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="请输入企业微信提供的 Secret"
                />
              </Form.Item>
            )}
          </div>
          <Form.Item name="remark" label="备注" rules={[basicRule]} className="mb-2!">
            <Input.TextArea maxLength={1000} showCount rows={3} placeholder="填写用途说明或维护备注" />
          </Form.Item>
        </div>
        <div className="border-border flex flex-wrap items-center justify-between gap-3 border-t px-5 py-4 sm:px-6">
          <span className="text-content-muted text-xs">
            企微 ID 和 Secret 用于 Hermes 收发消息，修改后重启对应网关生效。
          </span>
          {access.can('bot.update') && (
            <Button type="primary" htmlType="submit" loading={update.isPending}>
              保存基本信息
            </Button>
          )}
        </div>
      </Form>
    </section>
  )
}
